import type { ColumnDef } from '@tanstack/react-table';
import dayjs from 'dayjs';
import { round } from '@/utils/mathUtils';
import { useMemo } from 'react';
import {
  TrendingUp,
  Package,
  Calendar,
  Users,
  DollarSign,
  Percent,
  Gift,
  Hash,
  ShoppingCart,
  RotateCcw,
  Store,
  ArrowUpRight,
  ArrowDownRight,
} from 'lucide-react';

import { Image } from '@/components/custom-ui/Image';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useAppTheme } from '@/theme/themeProvider';
import { DataTable } from '@/UI/TableShadcn';
import { cn } from 'lib/utils';

import type { ICompanyItemInterface } from '../../api/CompanyItems.interface';

import ClientColumn from './ClientColumn';
import ItemColumn from './ItemColumn';

type Props = {
  items: ICompanyItemInterface[] | undefined;
};

function ItemsTable({ items }: Props) {
  const { isDark } = useAppTheme();

  const columns = useMemo<ColumnDef<ICompanyItemInterface>[]>(
    () => [
      {
        accessorKey: 'ID',
        header: () => (
          <div className="flex items-center justify-center gap-1">
            <Hash className="h-3.5 w-3.5 text-muted-foreground" />
            <span className="text-xs font-semibold">#</span>
          </div>
        ),
        size: 50,
        cell: (row) => {
          return (
            <div className="flex w-full items-center justify-center">
              <Badge
                variant="outline"
                className="h-7 w-10 justify-center bg-gradient-to-br from-muted/50 to-muted/30 font-mono text-xs font-medium"
              >
                {row.row.index + 1}
              </Badge>
            </div>
          );
        },
      },
      {
        accessorKey: 'ItemName',
        header: () => (
          <div className="flex items-center gap-2">
            <Package className="h-4 w-4 text-primary" />
            <span className="font-semibold">الصنف</span>
          </div>
        ),
        size: 250,
        cell: (row) => {
          return (
            <ItemColumn
              itemID={row?.row?.original?.ItemID}
              itemName={row?.row?.original?.ItemName}
            />
          );
        },
      },
      {
        accessorKey: 'ClientName',
        header: () => (
          <div className="flex items-center gap-2">
            <Users className="h-4 w-4 text-primary" />
            <span className="font-semibold">العميل</span>
          </div>
        ),
        size: 250,
        cell: (row) => {
          return (
            <ClientColumn
              clientName={row.row.original.ClientName}
              storeID={row.row.original.storeID}
              clientID={row.row.original.ClientID}
            />
          );
        },
      },
      {
        accessorKey: 'UnitPrice',
        header: () => (
          <div className="flex items-center justify-center gap-2">
            <DollarSign className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
            <span className="font-semibold">السعر</span>
          </div>
        ),
        size: 120,
        cell: ({ row }) => {
          const price = round(row?.original?.UnitPrice, 2);
          return (
            <div className="flex items-center justify-center">
              <div
                className={cn(
                  'flex items-center gap-1.5 rounded-lg px-3 py-1.5',
                  'bg-gradient-to-r from-emerald-50 to-emerald-100/50',
                  'dark:from-emerald-950/30 dark:to-emerald-900/20',
                  'border border-emerald-200 dark:border-emerald-800',
                )}
              >
                <span className="font-mono text-sm font-bold text-emerald-700 dark:text-emerald-300">
                  {price}
                </span>
                <span className="text-xs text-emerald-600 dark:text-emerald-400">
                  ج.م
                </span>
              </div>
            </div>
          );
        },
      },
      {
        accessorKey: 'TrDate',
        header: () => (
          <div className="flex items-center justify-center gap-2">
            <Calendar className="h-4 w-4 text-blue-600 dark:text-blue-400" />
            <span className="font-semibold">التاريخ</span>
          </div>
        ),
        size: 140,
        cell: (row) => {
          const date = dayjs(new Date(row.row.original.TrDate));
          return (
            <div className="flex items-center justify-center">
              <div
                className={cn(
                  'flex flex-col items-center rounded-lg px-3 py-1',
                  'bg-gradient-to-br from-blue-50 to-blue-100/50',
                  'dark:from-blue-950/30 dark:to-blue-900/20',
                  'border border-blue-200 dark:border-blue-800',
                )}
              >
                <span className="font-mono text-xs font-bold text-blue-700 dark:text-blue-300">
                  {date.format('YYYY/MM/DD')}
                </span>
                <span className="text-[10px] text-blue-600 dark:text-blue-400">
                  {date.format('dddd')}
                </span>
              </div>
            </div>
          );
        },
      },
      {
        accessorKey: 'Discount',
        header: () => (
          <div className="flex items-center justify-center gap-2">
            <Percent className="h-4 w-4 text-amber-600 dark:text-amber-400" />
            <span className="font-semibold">خصم</span>
          </div>
        ),
        size: 100,
        meta: { type: 'm' },
        cell: (row) => {
          const discount = round(row?.row?.original?.Discount || 0, 2);
          const hasDiscount = discount > 0;

          return (
            <div className="flex items-center justify-center">
              {hasDiscount ? (
                <Badge
                  className={cn(
                    'font-mono text-xs font-bold',
                    discount >= 20
                      ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                      : discount >= 10
                        ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
                        : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400',
                  )}
                >
                  {discount}%
                </Badge>
              ) : (
                <span className="text-muted-foreground">-</span>
              )}
            </div>
          );
        },
      },
      {
        accessorKey: 'Qty',
        header: () => (
          <div className="flex items-center justify-center gap-2">
            <Package className="h-4 w-4 text-violet-600 dark:text-violet-400" />
            <span className="font-semibold">الكمية</span>
          </div>
        ),
        size: 100,
        cell: (row) => {
          const qty = round(row?.row?.original?.Qty || 0, 2);
          return (
            <div className="flex items-center justify-center">
              <div
                className={cn(
                  'flex items-center gap-1 rounded-lg px-2.5 py-1',
                  'bg-gradient-to-r from-violet-50 to-violet-100/50',
                  'dark:from-violet-950/30 dark:to-violet-900/20',
                  'border border-violet-200 dark:border-violet-800',
                )}
              >
                <span className="font-mono text-sm font-bold text-violet-700 dark:text-violet-300">
                  {qty}
                </span>
              </div>
            </div>
          );
        },
      },
      {
        accessorKey: 'Bonus',
        header: () => (
          <div className="flex items-center justify-center gap-2">
            <Gift className="h-4 w-4 text-pink-600 dark:text-pink-400" />
            <span className="font-semibold">بونص</span>
          </div>
        ),
        size: 100,
        cell: (row) => {
          const bonus = round(row?.row?.original?.Bonus || 0, 2);
          const hasBonus = bonus > 0;

          return (
            <div className="flex items-center justify-center">
              {hasBonus ? (
                <Badge
                  variant="outline"
                  className="border-pink-300 bg-pink-50 font-mono text-xs font-bold text-pink-700 dark:border-pink-800 dark:bg-pink-950/30 dark:text-pink-400"
                >
                  +{bonus}
                </Badge>
              ) : (
                <span className="text-muted-foreground">-</span>
              )}
            </div>
          );
        },
      },
      {
        accessorKey: 'type',
        header: () => (
          <div className="flex items-center justify-center gap-2">
            <Store className="h-4 w-4 text-indigo-600 dark:text-indigo-400" />
            <span className="font-semibold">النوع</span>
          </div>
        ),
        size: 120,
        cell: (row) => {
          const type = row?.row?.original?.type?.trim();

          const getTypeConfig = (type: string) => {
            switch (type) {
              case 'بيع':
                return {
                  icon: <ShoppingCart className="h-3.5 w-3.5" />,
                  color: 'from-emerald-500 to-emerald-600 text-white',
                  darkColor: 'dark:from-emerald-600 dark:to-emerald-700',
                  label: 'بيع',
                  arrow: <ArrowUpRight className="h-3 w-3" />,
                };
              case 'شراء':
                return {
                  icon: <Package className="h-3.5 w-3.5" />,
                  color: 'from-blue-500 to-blue-600 text-white',
                  darkColor: 'dark:from-blue-600 dark:to-blue-700',
                  label: 'شراء',
                  arrow: <ArrowDownRight className="h-3 w-3" />,
                };
              case 'مرتجع عميل':
                return {
                  icon: <RotateCcw className="h-3.5 w-3.5" />,
                  color: 'from-red-500 to-red-600 text-white',
                  darkColor: 'dark:from-red-600 dark:to-red-700',
                  label: 'مرتجع عميل',
                  arrow: null,
                };
              default:
                return {
                  icon: <Store className="h-3.5 w-3.5" />,
                  color: 'from-orange-500 to-orange-600 text-white',
                  darkColor: 'dark:from-orange-600 dark:to-orange-700',
                  label: type || 'غير محدد',
                  arrow: null,
                };
            }
          };

          const config = getTypeConfig(type);

          return (
            <div className="flex items-center justify-center">
              <div
                className={cn(
                  'flex items-center gap-1.5 rounded-lg px-3 py-1.5',
                  'bg-gradient-to-r shadow-sm',
                  config.color,
                  config.darkColor,
                  'transition-all duration-200 hover:shadow-md',
                )}
              >
                {config.icon}
                <span className="text-xs font-bold">{config.label}</span>
                {config.arrow}
              </div>
            </div>
          );
        },
      },
    ],
    [],
  );

  // Calculate summary statistics
  const summaryStats = useMemo(() => {
    if (!items || items.length === 0) return null;

    const totalQty = items.reduce((sum, item) => sum + (item.Qty || 0), 0);
    const totalBonus = items.reduce((sum, item) => sum + (item.Bonus || 0), 0);
    const avgPrice =
      items.reduce((sum, item) => sum + (item.UnitPrice || 0), 0) /
      items.length;
    const avgDiscount =
      items.reduce((sum, item) => sum + (item.Discount || 0), 0) / items.length;

    return {
      totalQty: round(totalQty, 2),
      totalBonus: round(totalBonus, 2),
      avgPrice: round(avgPrice, 2),
      avgDiscount: round(avgDiscount, 2),
      totalItems: items.length,
    };
  }, [items]);

  return (
    <Card className="w-full border-0 bg-gradient-to-br from-background via-background to-muted/10 shadow-2xl">
      {summaryStats && (
        <CardHeader className="pb-4">
          <div className="flex flex-wrap items-center gap-3">
            <Badge
              variant="outline"
              className="border-blue-300 bg-gradient-to-r from-blue-50 to-blue-100 px-3 py-1 dark:border-blue-800 dark:from-blue-950/30 dark:to-blue-900/20"
            >
              <Package className="ml-1 h-3.5 w-3.5 text-blue-600 dark:text-blue-400" />
              <span className="text-xs font-semibold text-blue-700 dark:text-blue-300">
                إجمالي: {summaryStats.totalItems} عنصر
              </span>
            </Badge>

            <Badge
              variant="outline"
              className="border-violet-300 bg-gradient-to-r from-violet-50 to-violet-100 px-3 py-1 dark:border-violet-800 dark:from-violet-950/30 dark:to-violet-900/20"
            >
              <TrendingUp className="ml-1 h-3.5 w-3.5 text-violet-600 dark:text-violet-400" />
              <span className="text-xs font-semibold text-violet-700 dark:text-violet-300">
                الكمية: {summaryStats.totalQty}
              </span>
            </Badge>

            <Badge
              variant="outline"
              className="border-emerald-300 bg-gradient-to-r from-emerald-50 to-emerald-100 px-3 py-1 dark:border-emerald-800 dark:from-emerald-950/30 dark:to-emerald-900/20"
            >
              <DollarSign className="ml-1 h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" />
              <span className="text-xs font-semibold text-emerald-700 dark:text-emerald-300">
                متوسط السعر: {summaryStats.avgPrice} ج.م
              </span>
            </Badge>

            {summaryStats.totalBonus > 0 && (
              <Badge
                variant="outline"
                className="border-pink-300 bg-gradient-to-r from-pink-50 to-pink-100 px-3 py-1 dark:border-pink-800 dark:from-pink-950/30 dark:to-pink-900/20"
              >
                <Gift className="ml-1 h-3.5 w-3.5 text-pink-600 dark:text-pink-400" />
                <span className="text-xs font-semibold text-pink-700 dark:text-pink-300">
                  البونص: {summaryStats.totalBonus}
                </span>
              </Badge>
            )}

            {summaryStats.avgDiscount > 0 && (
              <Badge
                variant="outline"
                className="border-amber-300 bg-gradient-to-r from-amber-50 to-amber-100 px-3 py-1 dark:border-amber-800 dark:from-amber-950/30 dark:to-amber-900/20"
              >
                <Percent className="ml-1 h-3.5 w-3.5 text-amber-600 dark:text-amber-400" />
                <span className="text-xs font-semibold text-amber-700 dark:text-amber-300">
                  متوسط الخصم: {summaryStats.avgDiscount}%
                </span>
              </Badge>
            )}
          </div>
        </CardHeader>
      )}

      <CardContent className="w-full p-4">
        {items && items.length > 0 ? (
          <div className="overflow-hidden rounded-xl border border-border/50 bg-background/50 backdrop-blur-sm">
            <DataTable
              columns={columns}
              data={items}
              isPaginated
              isTopToolbar
              isRowSelection
              heightTable="55vh"
              visibleColumns={['Discount']}
            />
          </div>
        ) : (
          <div className="flex h-[55vh] flex-col items-center justify-center rounded-xl border border-border/30 bg-gradient-to-br from-muted/20 to-muted/10">
            <div className="flex flex-col items-center justify-center p-8">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-primary/20 to-primary/5 blur-3xl" />
                <Image
                  alt="No data"
                  src={
                    isDark ? '/images/svg/lost1.svg' : '/images/svg/lost.svg'
                  }
                  isBlurred
                  className="relative h-[250px] w-[250px] opacity-80 sm:h-[350px] sm:w-[350px]"
                />
              </div>
              <div className="mt-8 space-y-2 text-center">
                <h3 className="font-alex text-2xl text-muted-foreground sm:text-3xl">
                  لا توجد حركات علي هذا التصنيف
                </h3>
                <p className="text-sm text-muted-foreground/60">
                  قم بتحديد فلاتر أخرى لعرض البيانات
                </p>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default ItemsTable;
